import React from 'react';
import PropTypes from 'prop-types';
import momentWithAustinTimezone from 'common/util/momentWithAustinTimezone';
import lodashTimes from 'lodash/fp/times';

import {
  DATA_TABLE_SOURCE_HOUSEHOLD_KANBAN_APPLICATION_COLUMN,
} from 'common/dataTableConstants';

import {
  HOUSEHOLD_LISTING_STATUS_ACTIVE,
  HOUSEHOLD_APPLICATION_STATUS_PAID,
  HOUSEHOLD_APPLICATION_STATUS_INCOME_VERIFIED,
  HOUSEHOLD_APPLICATION_STATUS_PENDING_DOCUMENTS,
  HOUSEHOLD_APPLICATION_STATUS_REVIEWING_DOCUMENTS,
  HOUSEHOLD_APPLICATION_STATUS_PENDING_VERIFICATION,
  HOUSEHOLD_APPLICATION_STATUS_PENDING_TRANSUNION,
  HOUSEHOLD_APPLICATION_STATUS_DELIVERED,
  HOUSEHOLD_APPLICATION_STATUS_PENDING_LANDLORD,
  HOUSEHOLD_APPLICATION_STATUS_ACCEPTED,
  HOUSEHOLD_APPLICATION_STATUS_DEPOSIT_PAID,
  HOUSEHOLD_APPLICATION_STATUS_LEASE_DRAFT_PENDING,
  HOUSEHOLD_APPLICATION_STATUS_LEASE_SENT,
  HOUSEHOLD_APPLICATION_STATUS_LEASE_SIGNED,
} from 'common/householdConstants';
import {
  SR_APPLICATION_ASSEMBLY_LINE_NEXT_HOUSEHOLD_MONTHS_AHEAD,
} from 'common/applicationAssemblyLineConstants';

import SrFlex from 'client/components/SrFlex';
import SrLink from 'client/components/SrLink/SrLink';
import SrText from 'client/components/SrText';
import SrButton, {
  SR_BUTTON_THEME_ADOBE_GREY,
  SR_BUTTON_THEME_ADOBE_BLUE,
  SR_BUTTON_THEME_ADOBE_GREEN,
} from 'client/components/SrButton/SrButton';
import SrDataTableConnected from 'client/components/SrDataTableConnected';
import {
  sortPaidHouseholds,
} from 'client/components/SrHouseholdKanbanBoardApplicationColumn';
import {
  makeColumns,
} from 'client/components/SrHouseholdKanbanBoard';

const NEXT_APPLICANT = 'nextApplicant';
const NEXT_HOUSEHOLD = 'nextHousehold';
const NEXT_APPLICANT_TEXT = 'Next Applicant';
const NEXT_HOUSEHOLD_TEXT = 'Next Household';
const initialViewSettings = (moveInDateRanges) => ({
  loadAllData: true,
  customParams: {
    householdKanbanApplicationColumn: {
      moveInDateRanges,
      householdListingStatus: HOUSEHOLD_LISTING_STATUS_ACTIVE,
      includeHouseholdApplicationStatusList: [
        HOUSEHOLD_APPLICATION_STATUS_PAID,
        HOUSEHOLD_APPLICATION_STATUS_INCOME_VERIFIED,
        HOUSEHOLD_APPLICATION_STATUS_PENDING_DOCUMENTS,
        HOUSEHOLD_APPLICATION_STATUS_REVIEWING_DOCUMENTS,
        HOUSEHOLD_APPLICATION_STATUS_PENDING_VERIFICATION,
        HOUSEHOLD_APPLICATION_STATUS_PENDING_TRANSUNION,
        HOUSEHOLD_APPLICATION_STATUS_DELIVERED,
        HOUSEHOLD_APPLICATION_STATUS_PENDING_LANDLORD,
        HOUSEHOLD_APPLICATION_STATUS_ACCEPTED,
        HOUSEHOLD_APPLICATION_STATUS_DEPOSIT_PAID,
        HOUSEHOLD_APPLICATION_STATUS_LEASE_DRAFT_PENDING,
        HOUSEHOLD_APPLICATION_STATUS_LEASE_SENT,
        HOUSEHOLD_APPLICATION_STATUS_LEASE_SIGNED,
      ],
    },
  },
});

const SrApplicationAssemblyLineNextHouseholdButton = ({
  applicantUserId,
  applicants,
  updateApplicantUserId,
  householdListingId,
}) => {
  const date = momentWithAustinTimezone();
  const moveInDateRanges = [
    [
      date.startOf('month').toISOString(),
      date.endOf('month').toISOString(),
    ],
    ...lodashTimes(() => [
      date.add(1, 'months').startOf('month').toISOString(),
      date.endOf('month').toISOString(),
    ], SR_APPLICATION_ASSEMBLY_LINE_NEXT_HOUSEHOLD_MONTHS_AHEAD),
  ];
  const haveMoreThanOneApplicant = applicants.length > 1;
  const currentApplicantIndex = applicants.findIndex((applicant) => applicant.renterId === applicantUserId);
  const lastApplicantReached = currentApplicantIndex === applicants.length - 1;
  const nextApplicantsAreCompleted = applicants.slice(currentApplicantIndex + 1)
    .every((applicant) => applicant.progressPercent === 1);
  const allApplicantsAreCompleted = applicants.every((applicant) => applicant.progressPercent === 1);
  const nextType = haveMoreThanOneApplicant
    ? lastApplicantReached || nextApplicantsAreCompleted
      ? NEXT_HOUSEHOLD
      : NEXT_APPLICANT
    : NEXT_HOUSEHOLD;

  return (
    <SrFlex justify='center' width='170px' margin='auto auto 0'>
      <SrDataTableConnected
        itemsPropName='households'
        dataSource={DATA_TABLE_SOURCE_HOUSEHOLD_KANBAN_APPLICATION_COLUMN}
        rootViewId={DATA_TABLE_SOURCE_HOUSEHOLD_KANBAN_APPLICATION_COLUMN}
        initialViewSettings={initialViewSettings(moveInDateRanges)}
      >
        {({ households, dataTableApiState }) => {
          const columns = makeColumns({
            households: households,
          });
          const { activeHouseholds } = sortPaidHouseholds({
            households: columns[HOUSEHOLD_APPLICATION_STATUS_PAID].households,
            allHouseholds: households,
          });
          const activeHouseholdsFiltered = activeHouseholds
            .filter((household) =>
              household.listingApplications.some((application) => application.householdListingIsProcessing)
            );

          const onNextApplicantClick = () => {
            let nextApplicantId;

            for (let index = currentApplicantIndex + 1; index < applicants.length; index += 1) {
              const applicant = applicants[index];

              if (applicant.progressPercent !== 1) {
                nextApplicantId = applicant.renterId;
                break;
              }
            }

            if (!nextApplicantId) return;

            updateApplicantUserId(nextApplicantId);
          };

          const currentHouseholdIndex = activeHouseholdsFiltered
            .findIndex((household) =>
              household.listingApplications.some((application) => application.id === householdListingId)
            );
          const householdListings = activeHouseholdsFiltered[currentHouseholdIndex]
            ?.listingApplications.filter((application) => application.householdListingIsProcessing)
            || [];
          const currentHouseholdListingIndex = householdListings
            .findIndex((application) => application.id === householdListingId);
          const haveMoreListings =
            householdListings
              .filter((application) => application.householdListingIsProcessing)
              .length - 1
              > currentHouseholdListingIndex;
          const nextHouseholdHaveIsProcessingListings = activeHouseholdsFiltered[currentHouseholdIndex + 1]
            ?.listingApplications.findIndex((application) => application.householdListingIsProcessing);
          const nextHouseholdId = currentHouseholdIndex >= 0
            ? !haveMoreListings
              ? nextHouseholdHaveIsProcessingListings >= 0
                ? activeHouseholdsFiltered[currentHouseholdIndex + 1]
                  ?.listingApplications[nextHouseholdHaveIsProcessingListings].id
                : undefined
              : householdListings[currentHouseholdListingIndex + 1]?.id
            : undefined;
          const theme = nextType === NEXT_APPLICANT
            ? SR_BUTTON_THEME_ADOBE_BLUE
            : nextHouseholdId === undefined && !dataTableApiState.inProgress
              ? SR_BUTTON_THEME_ADOBE_GREY
              : SR_BUTTON_THEME_ADOBE_GREEN;
          const linkTextColor = theme === SR_BUTTON_THEME_ADOBE_GREY ? 'black' : 'white';
          const btnOnClick = nextType === NEXT_APPLICANT ? onNextApplicantClick : undefined;
          const btnDisabled = nextType === NEXT_APPLICANT
            ? false
            : nextHouseholdId === undefined || dataTableApiState.inProgress || !allApplicantsAreCompleted;

          return (
            <>
              <SrButton
                theme={theme}
                onClick={btnOnClick}
                disabled={btnDisabled}
              >
                {nextType === NEXT_HOUSEHOLD ? (
                  <SrLink to={`/admin/app-processing/${nextHouseholdId}`}>
                    <SrText size={21} color={linkTextColor} weight={800} font='greycliff'>
                      {NEXT_HOUSEHOLD_TEXT}
                    </SrText>
                  </SrLink>
                ) : NEXT_APPLICANT_TEXT}
              </SrButton>
            </>
          );
        }}
      </SrDataTableConnected>
    </SrFlex>
  );
};

SrApplicationAssemblyLineNextHouseholdButton.propTypes = {
  applicantUserId: PropTypes.number.isRequired,
  householdListingId: PropTypes.number.isRequired,
  applicants: PropTypes.array.isRequired,
  updateApplicantUserId: PropTypes.func.isRequired,
};

SrApplicationAssemblyLineNextHouseholdButton.displayName = 'SrApplicationAssemblyLineNextHouseholdButton';

export default SrApplicationAssemblyLineNextHouseholdButton;
